/**
 * Collection Search Module
 * Adds a search input to collection pages that fetches results via AJAX
 * and displays them on the current page without redirecting.
 * Integrates with the facet system to maintain search when filters change.
 */

(function() {
  'use strict';

  const CONFIG = {
    // Where to inject the search box - prioritize topbar for inline display
    targetSelectors: [
      '.facet-topbar',               // Topbar (inline with filters/sorting)
      '.facets--vertical',           // Sidebar filters (fallback)
      '.facets-block-wrapper--vertical',
      '#FacetFiltersForm'            // Drawer filters form
    ],
    // Minimum characters before searching
    minChars: 2,
    // Debounce delay in ms
    debounceDelay: 400,
    // ISBN pattern - matches 10 or 13 digit ISBNs (with optional hyphens)
    isbnPattern: /^[\d-]{10,17}$/
  };

  // Check if a search term looks like an ISBN
  function looksLikeIsbn(term) {
    // Remove hyphens and spaces for checking
    const cleaned = term.replace(/[-\s]/g, '');
    // ISBN-10 is 10 digits, ISBN-13 is 13 digits
    return /^\d{10}$|^\d{13}$/.test(cleaned);
  }

  // Normalize ISBN for comparison (remove hyphens and spaces)
  function normalizeIsbn(isbn) {
    return isbn.replace(/[-\s]/g, '');
  }

  // Search for products by ISBN in the current collection (client-side)
  function searchByIsbn(query, searchBox) {
    const loader = searchBox.querySelector('.collection-search__loader');
    const grid = getProductGrid();
    if (!grid) return false;

    const normalizedQuery = normalizeIsbn(query);

    // If we have original content, search within it
    // Otherwise search in current grid
    const searchContainer = document.createElement('div');
    searchContainer.innerHTML = originalContent || grid.innerHTML;

    const allCards = searchContainer.querySelectorAll('.product-card[data-isbn]');
    const matchingCards = [];

    allCards.forEach(card => {
      const isbn = card.dataset.isbn;
      if (isbn) {
        const normalizedIsbn = normalizeIsbn(isbn);
        // Check for exact match or partial match (for partial ISBN entry)
        if (normalizedIsbn === normalizedQuery || normalizedIsbn.includes(normalizedQuery)) {
          matchingCards.push(card.outerHTML);
        }
      }
    });

    if (matchingCards.length > 0) {
      // Show loader briefly
      if (loader) loader.hidden = false;

      setTimeout(() => {
        const gridClasses = getGridClasses();

        document.body.classList.add('collection-search-active');
        addSearchFilterChip(query);

        grid.innerHTML = `
          <div class="collection-search-results">
            <div class="collection-search-results__header">
              <p class="collection-search-results__count">
                Found ${matchingCards.length} result${matchingCards.length !== 1 ? 's' : ''} for ISBN "${escapeHtml(query)}"
              </p>
            </div>
            <motion-list class="${gridClasses}">
              ${matchingCards.join('')}
            </motion-list>
          </div>
        `;

        if (loader) loader.hidden = true;
      }, 100);

      return true; // Found ISBN matches
    }

    return false; // No ISBN matches found
  }

  // ---- Author Search Integration ----
  let authorData = null;
  let authorLoadPromise = null;
  const AUTHOR_FILTER_PARAM = 'filter.p.m.custom.authors';

  async function loadAuthorData() {
    if (authorData) return authorData;
    if (authorLoadPromise) return authorLoadPromise;

    authorLoadPromise = (async () => {
      try {
        const url = window.theme?.assets?.authorSearchData;
        if (!url) return [];
        const response = await fetch(url);
        if (!response.ok) return [];
        authorData = await response.json();
        return authorData;
      } catch (e) {
        return [];
      }
    })();

    return authorLoadPromise;
  }

  function findMatchingAuthor(query, authors) {
    if (!authors || !authors.length || query.length < 2) return null;

    const normalized = query.toLowerCase().trim();
    const words = normalized.split(/\s+/);

    let bestMatch = null;
    let bestScore = 0;

    for (const author of authors) {
      const name = author.n.toLowerCase();
      let score = 0;

      if (name === normalized) {
        score = 1000;
      } else if (name.startsWith(normalized)) {
        score = 100;
      } else if (words.length > 1 && words.every(w => name.includes(w))) {
        score = 50;
      }

      if (score > bestScore) {
        bestScore = score;
        bestMatch = author;
      }
    }

    // Only return strong matches (exact, starts-with, or all-words)
    return bestScore >= 50 ? bestMatch : null;
  }

  function applyAuthorFilter(author, searchBox) {
    const loader = searchBox.querySelector('.collection-search__loader');
    if (loader) loader.hidden = false;

    // Build URL with author filter on current collection
    const url = new URL(window.location.href);
    // Remove any existing author filter
    url.searchParams.delete(AUTHOR_FILTER_PARAM);
    // Add the matched author GID
    url.searchParams.set(AUTHOR_FILTER_PARAM, author.g);

    // Navigate to the filtered URL — the facet system handles the reload
    window.location.href = url.toString();
  }

  // Preload author data
  loadAuthorData();

  let abortController = null;
  let originalContent = null;
  let currentSearchTerm = '';
  let collectionHandle = '';

  // Create the search box HTML
  function createSearchBox(isTopbar = false) {
    const container = document.createElement('div');
    container.className = 'collection-search' + (isTopbar ? ' collection-search--topbar' : '');

    if (isTopbar) {
      // Inline compact search for topbar
      container.innerHTML = `
        <div class="collection-search__field field relative">
          <svg class="collection-search__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="search"
            id="collection-search-input"
            class="collection-search__input"
            placeholder="Search by title, author, or ISBN..."
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
          >
          <span class="collection-search__loader" hidden>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round">
                <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
              </path>
            </svg>
          </span>
          <button class="collection-search__clear" type="button" aria-label="Clear search" hidden>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      `;
    } else {
      // Full search box for sidebar/drawer
      container.innerHTML = `
        <div class="collection-search__wrapper">
          <label for="collection-search-input" class="collection-search__label text-base-xl font-medium">
            Search Products
          </label>
          <div class="collection-search__field field relative">
            <svg class="collection-search__icon absolute left-0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="search"
              id="collection-search-input"
              class="collection-search__input input"
              placeholder="Search by title, author, or ISBN..."
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
            >
            <span class="collection-search__loader absolute right-0" hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
                <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                </path>
              </svg>
            </span>
            <button class="collection-search__clear absolute right-0" type="button" aria-label="Clear search" hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      `;
    }
    return container;
  }

  // Check if we're on a collection page
  function isCollectionPage() {
    return window.location.pathname.includes('/collections/');
  }

  // Get current collection handle from URL
  function getCollectionHandle() {
    const match = window.location.pathname.match(/\/collections\/([^/?]+)/);
    return match ? match[1] : null;
  }

  // Get the product grid container
  function getProductGrid() {
    return document.getElementById('ProductGridContainer') ||
           document.querySelector('.collection-product-list') ||
           document.querySelector('[data-products]');
  }

  // Save original content for restoration
  function saveOriginalContent() {
    if (originalContent) return; // Already saved

    const grid = getProductGrid();
    if (grid) {
      originalContent = grid.innerHTML;
    }
  }

  // Restore original content
  function restoreOriginalContent() {
    const grid = getProductGrid();
    if (grid && originalContent) {
      grid.innerHTML = originalContent;
      // Remove search active indicator
      document.body.classList.remove('collection-search-active');
      // Remove search filter chip
      removeSearchFilterChip();
    }
  }

  // Add search filter chip to active filters area
  function addSearchFilterChip(query) {
    removeSearchFilterChip(); // Remove existing chip first

    const activeFiltersContainer = document.getElementById('FacetFiltersActive');
    if (!activeFiltersContainer) return;

    const chip = document.createElement('div');
    chip.className = 'removable-facet removable-facet--search flex items-center justify-between gap-3 lg:gap-4 text-xs lg:text-sm leading-none';
    chip.setAttribute('data-search-filter', '');
    chip.innerHTML = `
      <div class="facet-title flex items-center gap-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <span>"${escapeHtml(query)}"</span>
      </div>
      <button type="button" class="block relative" aria-label="Clear search" data-clear-search>
        <span class="btn-text">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"></path>
          </svg>
        </span>
      </button>
    `;

    // Insert at beginning of active filters
    activeFiltersContainer.insertBefore(chip, activeFiltersContainer.firstChild);

    // Add click handler to clear search
    chip.querySelector('[data-clear-search]').addEventListener('click', () => {
      const searchBox = document.querySelector('.collection-search');
      if (searchBox) {
        clearSearch(searchBox);
      }
    });
  }

  // Remove search filter chip
  function removeSearchFilterChip() {
    const chip = document.querySelector('[data-search-filter]');
    if (chip) {
      chip.remove();
    }
  }

  // Escape HTML to prevent XSS
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // Build search URL with current filters
  function buildSearchUrl(query, handle) {
    const url = new URL(window.location.origin + '/search');
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', query);
    url.searchParams.set('filter.p.collection_handle', handle);

    // Add any existing filters from the current URL
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      // Include filter parameters but skip sort_by and page
      if (key.startsWith('filter.') && key !== 'filter.p.collection_handle') {
        url.searchParams.append(key, value);
      }
    });

    // Request section rendering for cleaner results
    url.searchParams.set('section_id', 'main-search');
    return url.toString();
  }

  // Get grid classes from the current collection grid
  function getGridClasses() {
    const existingGrid = document.querySelector('#ProductGridContainer motion-list, #ProductGridContainer .card-grid');
    if (existingGrid) {
      // Extract card-grid classes
      const classes = Array.from(existingGrid.classList).filter(c =>
        c.startsWith('card-grid') || c.startsWith('mobile:card-grid') || c === 'grid' || c === 'relative'
      );
      return classes.join(' ');
    }
    // Default fallback
    return 'card-grid card-grid--4 mobile:card-grid--2 grid relative';
  }

  // Fetch and display search results
  async function fetchSearchResults(query, handle, searchBox) {
    const loader = searchBox.querySelector('.collection-search__loader');
    const grid = getProductGrid();

    if (!grid) return;

    // Abort any pending request
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    // Show loader
    if (loader) loader.hidden = false;

    try {
      const url = buildSearchUrl(query, handle);
      const response = await fetch(url, { signal: abortController.signal });

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract product cards from the search page
      // Look for product cards in various containers
      let productCards = doc.querySelectorAll('.product-card');

      // If no product-card elements found directly, try looking in grid containers
      if (productCards.length === 0) {
        const searchGrid = doc.querySelector('.search-results__products, .product-grid, [data-products], .card-grid');
        if (searchGrid) {
          productCards = searchGrid.querySelectorAll('.product-card, .card-product, [data-product-card]');
        }
      }

      // Get grid classes from current collection
      const gridClasses = getGridClasses();

      // Build results HTML
      if (productCards.length > 0) {
        // Add search active indicator
        document.body.classList.add('collection-search-active');

        // Add filter chip
        addSearchFilterChip(query);

        // Create a results container with the same structure as original grid
        const resultsHtml = `
          <div class="collection-search-results">
            <div class="collection-search-results__header">
              <p class="collection-search-results__count">
                Found ${productCards.length} result${productCards.length !== 1 ? 's' : ''} for "${escapeHtml(query)}"
              </p>
            </div>
            <motion-list class="${gridClasses}">
              ${Array.from(productCards).map(card => card.outerHTML).join('')}
            </motion-list>
          </div>
        `;

        grid.innerHTML = resultsHtml;
      } else {
        // No results found
        document.body.classList.add('collection-search-active');
        addSearchFilterChip(query);

        grid.innerHTML = `
          <div class="collection-search-results collection-search-results--empty">
            <div class="empty-state grid items-center justify-center text-center gap-7d5">
              <div class="flex flex-col items-center gap-3">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p class="heading leading-none tracking-tight">No results found</p>
                <p>No products found for "${escapeHtml(query)}" in this collection</p>
              </div>
              <button type="button" class="button button--primary icon-with-text" data-clear-collection-search>
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 6L6 18M6 6l12 12"></path>
                  </svg>
                  Clear search
                </span>
              </button>
            </div>
          </div>
        `;

        // Add click handler to clear button
        const clearBtn = grid.querySelector('[data-clear-collection-search]');
        if (clearBtn) {
          clearBtn.addEventListener('click', () => {
            clearSearch(searchBox);
          });
        }
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Collection search error:', error);
      // On error, keep showing current content
    } finally {
      // Hide loader
      if (loader) loader.hidden = true;
    }
  }

  // Debounce function
  function debounce(fn, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  // Clear search input and restore content
  function clearSearch(searchBox) {
    const input = searchBox.querySelector('.collection-search__input');
    const clearBtn = searchBox.querySelector('.collection-search__clear');
    const loader = searchBox.querySelector('.collection-search__loader');

    if (input) input.value = '';
    if (clearBtn) clearBtn.hidden = true;
    if (loader) loader.hidden = true;
    searchBox.classList.remove('has-value');

    currentSearchTerm = '';

    // Restore original content
    restoreOriginalContent();
  }

  // Initialize collection search
  function initCollectionSearch() {
    // Only init on collection pages
    if (!isCollectionPage()) return;

    collectionHandle = getCollectionHandle();
    if (!collectionHandle) return;

    // Find a suitable container for the search box
    let targetContainer = null;
    for (const selector of CONFIG.targetSelectors) {
      targetContainer = document.querySelector(selector);
      if (targetContainer) break;
    }

    // If no sidebar/drawer, try to add near the topbar
    if (!targetContainer) {
      targetContainer = document.querySelector('.facets-active, .collection__topbar');
    }

    if (!targetContainer) return;

    // Check if already initialized
    if (targetContainer.querySelector('.collection-search')) return;

    // Save original grid content before any search
    saveOriginalContent();

    // Determine if this is the topbar
    const isTopbar = targetContainer.classList.contains('facet-topbar');

    // Create and insert search box
    const searchBox = createSearchBox(isTopbar);

    // Insert at the appropriate location
    if (isTopbar) {
      // For topbar, append to the end (right side where sorting was)
      targetContainer.appendChild(searchBox);
    } else if (targetContainer.classList.contains('facets--vertical') ||
        targetContainer.classList.contains('facets-block-wrapper--vertical')) {
      // For sidebar, insert at the very top
      targetContainer.insertBefore(searchBox, targetContainer.firstChild);
    } else {
      // For other locations, append
      targetContainer.appendChild(searchBox);
    }

    const input = searchBox.querySelector('.collection-search__input');
    const clearBtn = searchBox.querySelector('.collection-search__clear');

    // Debounced search function
    const debouncedSearch = debounce(async (term) => {
      if (term.length >= CONFIG.minChars) {
        currentSearchTerm = term;

        // If the term looks like an ISBN, try ISBN search first
        if (looksLikeIsbn(term)) {
          const foundIsbn = searchByIsbn(term, searchBox);
          if (foundIsbn) return; // ISBN match found, don't do regular search
        }

        // Check for author match — apply filter and reload if found
        const authors = await loadAuthorData();
        const matchedAuthor = findMatchingAuthor(term, authors);
        if (matchedAuthor) {
          applyAuthorFilter(matchedAuthor, searchBox);
          return; // Navigating to filtered URL
        }

        // Regular text search
        fetchSearchResults(term, collectionHandle, searchBox);
      }
    }, CONFIG.debounceDelay);

    // Event listeners
    input.addEventListener('input', (e) => {
      const term = e.target.value.trim();

      // Add/remove active class for styling and show/hide clear button
      if (term) {
        searchBox.classList.add('has-value');
        clearBtn.hidden = false;
      } else {
        searchBox.classList.remove('has-value');
        clearBtn.hidden = true;
        // Restore original content when input is cleared
        restoreOriginalContent();
        return;
      }

      // Trigger debounced search
      if (term.length >= CONFIG.minChars) {
        debouncedSearch(term);
      } else if (term.length === 0) {
        restoreOriginalContent();
      }
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        clearSearch(searchBox);
        input.blur();
      }
    });

    clearBtn.addEventListener('click', () => {
      clearSearch(searchBox);
      input.focus();
    });
  }

  // Listen for facet/filter changes and re-run search if active
  function setupFacetIntegration() {
    // Listen for collection reload events (from facet changes)
    document.addEventListener('collection:reloaded', () => {
      // If we have an active search, we need to update original content
      // and re-run the search with new filters
      if (currentSearchTerm) {
        // Update the original content cache with the new filtered content
        const grid = getProductGrid();
        if (grid && !document.body.classList.contains('collection-search-active')) {
          originalContent = grid.innerHTML;
        }

        // Re-run the search with the current term
        const searchBox = document.querySelector('.collection-search');
        if (searchBox && currentSearchTerm.length >= CONFIG.minChars) {
          fetchSearchResults(currentSearchTerm, collectionHandle, searchBox);
        }
      } else {
        // No active search, just update original content cache
        originalContent = null;
        saveOriginalContent();
      }
    });

    // Also handle when facet form submits (to intercept before reload)
    const facetForm = document.querySelector('form[is="facet-form"]');
    if (facetForm) {
      // Store original renderSection method
      const originalRenderSection = facetForm.renderSection;

      if (originalRenderSection && typeof originalRenderSection === 'function') {
        facetForm.renderSection = function(url, event) {
          // If we have an active search, we need to handle it specially
          if (currentSearchTerm) {
            // Clear the search visual state but keep the term
            document.body.classList.remove('collection-search-active');

            // Call original render
            originalRenderSection.call(this, url, event);
          } else {
            // No search active, normal behavior
            originalRenderSection.call(this, url, event);
          }
        };
      }
    }
  }

  // Add CSS for styling
  function addStyles() {
    if (document.getElementById('collection-search-dynamic-styles')) return;

    const style = document.createElement('style');
    style.id = 'collection-search-dynamic-styles';
    style.textContent = `
      /* Hide browser's native search clear button */
      .collection-search__input::-webkit-search-cancel-button,
      .collection-search__input::-webkit-search-decoration {
        -webkit-appearance: none;
        appearance: none;
      }

      /* Ensure hidden elements stay hidden */
      .collection-search__clear[hidden],
      .collection-search__loader[hidden] {
        display: none !important;
      }

      /* Loader positioning */
      .collection-search__loader {
        position: absolute;
        right: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .collection-search--topbar .collection-search__loader {
        right: 0.5rem;
      }

      /* Hide clear button when loader is showing */
      .collection-search__loader:not([hidden]) + .collection-search__clear {
        display: none !important;
      }

      /* Search results styling */
      .collection-search-results {
        padding: 1rem 0;
      }

      .collection-search-results__header {
        margin-bottom: 1.5rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid rgb(var(--color-border));
      }

      .collection-search-results__count {
        font-size: var(--text-sm, 0.875rem);
        color: rgb(var(--color-foreground) / 0.7);
      }

      .collection-search-results--empty {
        text-align: center;
        padding: 3rem 1rem;
      }

      .collection-search-results--empty .collection-search-results__header {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
      }

      /* Search filter chip styling */
      .removable-facet--search {
        background-color: rgb(var(--color-foreground) / 0.05);
        padding: 0.5rem 0.75rem;
        border-radius: var(--rounded-button, 4px);
      }

      .removable-facet--search svg {
        flex-shrink: 0;
      }

      .removable-facet--search [data-clear-search] {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: none;
        background: none;
        cursor: pointer;
        opacity: 0.5;
        transition: opacity 0.2s;
      }

      .removable-facet--search [data-clear-search]:hover {
        opacity: 1;
      }
    `;
    document.head.appendChild(style);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      addStyles();
      initCollectionSearch();
      setupFacetIntegration();
    });
  } else {
    addStyles();
    initCollectionSearch();
    setupFacetIntegration();
  }

  // Handle Shopify section loads
  document.addEventListener('shopify:section:load', () => {
    addStyles();
    originalContent = null; // Reset original content cache
    initCollectionSearch();
    setupFacetIntegration();
  });

})();
